import React, { useState, useRef, useEffect } from 'react';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { Box, Typography, Grid, Button } from '@mui/material';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import axios from 'axios';
import { decrypt } from '../../../Security';
import {FFmpeg} from '@ffmpeg/ffmpeg';

const transcodeToMp4 = async (webmBlob) => {
  const ffmpeg = new FFmpeg();

  try {
    // Load the FFmpeg core
    await ffmpeg.load();

    // Convert the Blob to Uint8Array for FFmpeg input
    const webmData = new Uint8Array(await webmBlob.arrayBuffer());
    await ffmpeg.writeFile("input.webm", webmData);

    // Execute FFmpeg command to transcode .webm to .mp4
    await ffmpeg.exec(["-i", "input.webm", "output.mp4"]);

    // Read the output file and convert it back to a Blob
    const mp4Data = await ffmpeg.readFile("output.mp4");
    const mp4Blob = new Blob([mp4Data.buffer], { type: "video/mp4" });

    return mp4Blob;
  } catch (error) {
    console.error("Error during transcoding: ", error);
    throw error;
  } finally {
    // Optionally terminate the FFmpeg instance to free resources
    ffmpeg.terminate();
  }
};


const VideoUploadComponent = ({ videos, setVideos, handleDeleteUpload, createMode }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    setIsRecording(true);
    setTimer(0);
    recordedChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      // mediaRecorderRef.current = new MediaRecorder(stream);
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'video/mp4; codecs=avc1.42E01E,mp4a.40.2' });

      mediaRecorderRef.current.ondataavailable = (e) => {
        recordedChunksRef.current.push(e.data);
      };
      mediaRecorderRef.current.onstop = async () => {
        const videoBlob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const videoUrl = URL.createObjectURL(videoBlob);
        // const mp4Blob = await transcodeToMp4(videoBlob);
        uploadVideo(videoBlob).then((uploadedUrl) => {
          setVideos((prevVideos) => [...prevVideos, { url: uploadedUrl, blob: videoBlob }]);
        });
      };

      mediaRecorderRef.current.start();
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing media devices.', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }

    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
    }

    setIsRecording(false);
    clearInterval(timerRef.current);
  };

  const handleVideoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const videoUrl = URL.createObjectURL(file);
      uploadVideo(file).then((uploadedUrl) => {
        setVideos((prevVideos) => [...prevVideos, { url: uploadedUrl, blob: file }]);
      });
    }
  };

  

  const uploadVideo = async (videoBlob) => {
    const formData = new FormData();
    
    formData.append('video', videoBlob, videoBlob.name || 'recorded-video.webm');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/media/upload-video`,
        formData,
        { withCredentials: true }
      );
      const url = decrypt(response.data).videoUrl;
      return url;
    } catch (error) {
      console.error('Error uploading video:', error);
    }
  };

  const handleDelete = (index) => {
    const videoToDelete = videos[index];
    if (videoToDelete) {
      handleDeleteUpload(videoToDelete.url).finally(() => {
        setVideos((prevVideos) => prevVideos.filter((_, i) => i !== index));
      });
    }
  };

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }

      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach((track) => track.stop());
      }
      clearInterval(timerRef.current);
    };
  }, []);

  return (
    <Box sx={{ padding: 2, border: 1, borderColor: 'grey.300', borderRadius: 2, width: '100%' }}>
      <Box sx={{ marginBottom: 2 }}>
        {videos.map((video, index) => (
          <Box key={index} sx={{ marginBottom: 2 }}>
            
            <video controls src={video?.url || video} style={{ width: '100%', height: 500 }} />
            {  <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={() => handleDelete(index)}
            >
              Delete
            </Button>}
          </Box>
        ))}
      </Box>
      { (
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            
            {isRecording
              ? `Recording Time: ${Math.floor(timer / 60)}:${String(timer % 60).padStart(2, '0')}`
              : (!createMode?'Add or Record Videos to updated this post':'Add or Record New Videos')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                component="label"
                color="success"
                fullWidth
                disabled={isRecording}
              >
                Choose File
                <input
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={handleVideoChange}
                />
              </Button>
            </Grid>
            <Grid item xs={6}>
              {isRecording ? (
                <Button variant="contained" color="error" fullWidth onClick={stopRecording}>
                  Stop Recording
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  onClick={startRecording}
                >
                  Record Video
                </Button>
              )}
            </Grid>
          </Grid>
          {isRecording && (
            <Box sx={{ marginTop: 2 }}>
              <video ref={videoRef} style={{ width: '100%', height: 'auto' }} muted autoPlay playsInline />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VideoUploadComponent;
